import { Injectable, OnDestroy } from '@angular/core';
import { State, Store } from '@ngrx/store';
import * as RiderAuthenticationActions from '@uzurv/rideWss/app/store/rider-authentication/rider-authentication.action';
import * as RiderAuthenticationSelectors from '@uzurv/rideWss/app/store/rider-authentication/rider-authentication.selector';
import { RiderAuthenticationState } from '@uzurv/rideWss/app/store/rider-authentication/rider-authentication.state';
import { AuthenticationStatus } from '@uzurv/rideWss/auth/constants';
import { DeviceInstallation, RiderDetail } from '@uzurv/rideWss/clara';
import { MobilityProfileDetail } from '@uzurv/rideWss/clara/models/mobility-profile-detail';
import { Subject, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthenticatedRiderService implements OnDestroy {
  #authenticationStatus: AuthenticationStatus;
  #authenticatedRider: RiderDetail;
  #mobilityProfileDetail: MobilityProfileDetail;
  #currentDeviceInstallation: DeviceInstallation;

  readonly destroyed$ = new Subject<boolean>();
  readonly authenticationStatus$ = this.store.select(RiderAuthenticationSelectors.getAuthenticationStatus);
  readonly authenticatedRider$ = this.store.select(RiderAuthenticationSelectors.getAuthenticatedRider);
  readonly mobilityProfileDetail$ = this.store.select(RiderAuthenticationSelectors.getMobilityProfileDetail);
  readonly currentDeviceInstallation$ = this.store.select(RiderAuthenticationSelectors.getCurrentDeviceInstallation);

  constructor(private store: Store<State<RiderAuthenticationState>>) {
    this.authenticationStatus$.pipe(takeUntil(this.destroyed$)).subscribe(authenticationStatus => {
      this.#authenticationStatus = authenticationStatus;
    });

    this.authenticatedRider$.pipe(takeUntil(this.destroyed$)).subscribe(authenticateRider => {
      this.#authenticatedRider = authenticateRider;
    });

    this.currentDeviceInstallation$.pipe(takeUntil(this.destroyed$)).subscribe(currentDeviceInstallation => {
      this.#currentDeviceInstallation = currentDeviceInstallation;
    });

    this.mobilityProfileDetail$.pipe(takeUntil(this.destroyed$)).subscribe(mobilityProfileDetail => {
      this.#mobilityProfileDetail = mobilityProfileDetail;
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  logout(): void {
    this.store.dispatch(RiderAuthenticationActions.logout());
  }

  getLoggedInRider(): RiderDetail {
    return this.#authenticatedRider;
  }

  getLoggedInRiderId(): number {
    return this.#authenticatedRider?.id;
  }

  isLoggedIn(): boolean {
    return this.#authenticationStatus === AuthenticationStatus.LOGGED_IN;
  }

  isRiderSuspended(): boolean {
    return this.#authenticatedRider?.suspended;
  }

  getLoggedInUserFirstName(): string {
    return this.#authenticatedRider?.firstName;
  }

  getLoggedInUserLastName(): string {
    return this.#authenticatedRider?.lastName;
  }

  getLoggedInUserEmailAddress(): string {
    return this.#authenticatedRider?.emailAddress;
  }

  getLoggedInUserDateOfBirth(): Date {
    return this.#authenticatedRider?.dateOfBirth;
  }

  getLoggedInUserPhoneNumber(): string {
    return this.#authenticatedRider?.phoneNumber;
  }

  getCurrentDeviceInstallation(): DeviceInstallation {
    return this.#currentDeviceInstallation;
  }

  getMobilityProfileDetail() {
    return this.#mobilityProfileDetail;
  }
}
